import React from 'react';
import { toneClasses } from '../../lib/healthAnalysis';
import { CircleHelp } from 'lucide-react';

/**
 * Premium metric card showing a single calculated marker with category badge.
 *
 * Props:
 *   icon: lucide React component
 *   label: marker name
 *   value: computed number/string OR null
 *   unit: e.g. 'kg/m²'
 *   category: { label, tone } or null
 *   subtitle: small caption (e.g. classification thresholds)
 *   missingHint: text shown when value is null
 *   testId: data-testid
 */
const MetricCard = ({ icon: Icon, label, value, unit, category, subtitle, missingHint, testId }) => {
  const missing = value == null || value === '';
  const tone = toneClasses(category?.tone);

  return (
    <div
      className={`bg-slate-900/60 backdrop-blur-sm border ${missing ? 'border-slate-800' : tone.border} rounded-2xl p-4 sm:p-5 transition-colors`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${missing ? 'text-slate-500' : tone.text}`} aria-hidden="true" />}
          <span className="text-slate-300 text-xs sm:text-sm font-medium">{label}</span>
        </div>
        {category && (
          <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${tone.bg} ${tone.text} ${tone.border} border whitespace-nowrap`}>
            {category.label}
          </span>
        )}
      </div>
      {missing ? (
        <div className="flex items-start gap-2 py-2">
          <CircleHelp className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <p className="text-slate-500 text-xs leading-relaxed">{missingHint || 'Insufficient Data'}</p>
        </div>
      ) : (
        <div className="flex items-baseline gap-1.5">
          <span className={`text-2xl sm:text-3xl font-bold ${tone.text} tabular-nums`}>{value}</span>
          {unit && <span className="text-slate-500 text-xs sm:text-sm">{unit}</span>}
        </div>
      )}
      {subtitle && !missing && <p className="text-slate-500 text-[10px] sm:text-xs mt-2 leading-snug">{subtitle}</p>}
    </div>
  );
};

export default MetricCard;
